import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useSettingsStore } from '@/stores/settings-store';

// ── Editor Type Detection ────────────────────────────────────

export type EditorType = 'text' | 'number' | 'boolean' | 'date' | 'time' | 'timestamp' | 'json' | 'multiline';

export function getEditorType(colType: string): EditorType {
  const t = colType.toLowerCase();

  if (['bool', 'boolean', 'bit'].includes(t)) return 'boolean';
  if (
    ['integer', 'int', 'int2', 'int4', 'int8', 'bigint', 'smallint', 'serial', 'bigserial',
     'float', 'float4', 'float8', 'double', 'double precision', 'decimal', 'numeric',
     'real', 'money', 'number', 'tinyint', 'mediumint'].some(x => t.includes(x))
  ) return 'number';
  if (t === 'date') return 'date';
  if (t.includes('timestamp') || t.includes('datetime')) return 'timestamp';
  if (t.includes('time') && !t.includes('stamp')) return 'time';
  if (['json', 'jsonb'].includes(t)) return 'json';
  if (['text', 'clob', 'mediumtext', 'longtext', 'ntext'].some(x => t.includes(x))) return 'multiline';

  return 'text';
}

// ── Props ────────────────────────────────────────────────────

interface CellEditorProps {
  value: unknown;
  columnName: string;
  columnType: string;
  initialKey?: string; // If user started typing to open editor
  forceModal?: boolean; // Force modal editor regardless of type
  cellRect: DOMRect;
  onSave: (value: unknown) => void;
  onCancel: () => void;
  onSaveAndMove: (value: unknown, direction: 'down' | 'right') => void;
}

// ── Main Component ───────────────────────────────────────────

export function CellEditor({ value, columnType, initialKey, forceModal, cellRect, onSave, onCancel, onSaveAndMove }: CellEditorProps) {
  const editorType = getEditorType(columnType);
  const gridFontSize = useSettingsStore((s) => s.getNumber('grid.fontSize', 12));
  const gridFontFamily = useSettingsStore((s) => s.get('grid.fontFamily', "'JetBrains Mono', monospace"));
  const fontStyle = { fontSize: `${gridFontSize}px`, fontFamily: gridFontFamily };

  // Force modal editor when requested (from "Edit in Modal" context menu)
  if (forceModal && editorType !== 'boolean') {
    return createPortal(
      <ModalEditor
        value={value}
        editorType="multiline"
        fontStyle={fontStyle}
        onSave={onSave}
        onCancel={onCancel}
      />,
      document.body,
    );
  }

  // For boolean type, use a simple dropdown
  if (editorType === 'boolean') {
    return createPortal(
      <BooleanEditor
        value={value}
        cellRect={cellRect}
        fontStyle={fontStyle}
        onSave={onSave}
        onCancel={onCancel}
        onSaveAndMove={onSaveAndMove}
      />,
      document.body,
    );
  }

  // For json/multiline, use a modal editor
  if (editorType === 'json' || editorType === 'multiline') {
    return createPortal(
      <ModalEditor
        value={value}
        editorType={editorType}
        fontStyle={fontStyle}
        onSave={onSave}
        onCancel={onCancel}
      />,
      document.body,
    );
  }

  // For all other types, use an in-place input
  return createPortal(
    <InPlaceEditor
      value={value}
      editorType={editorType}
      initialKey={initialKey}
      cellRect={cellRect}
      fontStyle={fontStyle}
      onSave={onSave}
      onCancel={onCancel}
      onSaveAndMove={onSaveAndMove}
    />,
    document.body,
  );
}

// ── In-Place Editor (text, number, date, time, timestamp) ────

interface InPlaceEditorProps {
  value: unknown;
  editorType: EditorType;
  initialKey?: string;
  cellRect: DOMRect;
  fontStyle: React.CSSProperties;
  onSave: (value: unknown) => void;
  onCancel: () => void;
  onSaveAndMove: (value: unknown, direction: 'down' | 'right') => void;
}

function formatValueForInput(value: unknown, editorType: EditorType): string {
  if (value === null || value === undefined) return '';
  if (editorType === 'date' && typeof value === 'string') {
    // Try to format as YYYY-MM-DD for date input
    const d = new Date(value);
    if (!isNaN(d.getTime())) {
      return d.toISOString().split('T')[0];
    }
  }
  if (editorType === 'timestamp' && typeof value === 'string') {
    // Format as datetime-local value
    const d = new Date(value);
    if (!isNaN(d.getTime())) {
      return d.toISOString().slice(0, 16);
    }
  }
  return String(value);
}

function parseValueFromInput(text: string, editorType: EditorType): unknown {
  if (text === '') return null;
  if (editorType === 'number') {
    const n = Number(text);
    return isNaN(n) ? text : n;
  }
  return text;
}

function InPlaceEditor({ value, editorType, initialKey, cellRect, fontStyle, onSave, onCancel, onSaveAndMove }: InPlaceEditorProps) {
  const initialText = initialKey !== undefined ? initialKey : formatValueForInput(value, editorType);
  const [editValue, setEditValue] = useState(initialText);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    input.focus();
    if (initialKey !== undefined) {
      // User started typing: cursor at end
      input.selectionStart = input.value.length;
      input.selectionEnd = input.value.length;
    } else {
      // Double-click or F2: select all
      input.select();
    }
  }, [initialKey]);

  const save = useCallback(() => {
    onSave(parseValueFromInput(editValue, editorType));
  }, [editValue, editorType, onSave]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      onSaveAndMove(parseValueFromInput(editValue, editorType), 'down');
    } else if (e.key === 'Tab') {
      e.preventDefault();
      e.stopPropagation();
      onSaveAndMove(parseValueFromInput(editValue, editorType), 'right');
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onCancel();
    }
  }, [editValue, editorType, onSaveAndMove, onCancel]);

  const inputType = editorType === 'date' ? 'date'
    : editorType === 'time' ? 'time'
    : editorType === 'timestamp' ? 'datetime-local'
    : editorType === 'number' ? 'text' // Use text for number to allow scientific notation
    : 'text';

  return (
    <div
      className="fixed z-[200]"
      style={{
        top: cellRect.top,
        left: cellRect.left,
        width: Math.max(cellRect.width, 120),
        height: cellRect.height,
      }}
    >
      <input
        ref={inputRef}
        type={inputType}
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={save}
        className="w-full h-full bg-[var(--dbgo-input)] border-2 border-[#4a7fff] px-2 text-[var(--text-primary)] outline-none"
        style={{ boxSizing: 'border-box', ...fontStyle }}
      />
    </div>
  );
}

// ── Boolean Editor ───────────────────────────────────────────

interface BooleanEditorProps {
  value: unknown;
  cellRect: DOMRect;
  fontStyle: React.CSSProperties;
  onSave: (value: unknown) => void;
  onCancel: () => void;
  onSaveAndMove: (value: unknown, direction: 'down' | 'right') => void;
}

function BooleanEditor({ value, cellRect, fontStyle, onCancel, onSaveAndMove }: BooleanEditorProps) {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    menuRef.current?.focus();
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
  }, [onCancel]);

  const options = [
    { label: 'TRUE', val: true },
    { label: 'FALSE', val: false },
    { label: 'NULL', val: null },
  ];

  const currentStr = value === null || value === undefined ? 'NULL' : value ? 'TRUE' : 'FALSE';

  return (
    <div
      ref={menuRef}
      className="fixed z-[200] bg-[var(--dbgo-panel)] border border-[var(--border-default)] rounded shadow-lg py-1 min-w-[100px] outline-none"
      style={{
        top: cellRect.bottom,
        left: cellRect.left,
      }}
      tabIndex={-1}
      onKeyDown={handleKeyDown}
      onBlur={(e) => {
        if (!menuRef.current?.contains(e.relatedTarget)) {
          onCancel();
        }
      }}
    >
      {options.map((opt) => (
        <button
          key={opt.label}
          onClick={() => onSaveAndMove(opt.val, 'down')}
          className={`w-full text-left px-3 py-1 transition-colors ${
            currentStr === opt.label
              ? 'text-[var(--text-primary)] bg-[var(--dbgo-grid-row-selected)]'
              : 'text-[var(--text-secondary)] hover:bg-[var(--dbgo-tab-hover)]'
          }`}
          style={fontStyle}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ── Modal Editor (JSON / Multiline) ──────────────────────────

interface ModalEditorProps {
  value: unknown;
  editorType: 'json' | 'multiline';
  fontStyle: React.CSSProperties;
  onSave: (value: unknown) => void;
  onCancel: () => void;
}

function ModalEditor({ value, editorType, fontStyle, onSave, onCancel }: ModalEditorProps) {
  const initialText = value === null || value === undefined
    ? ''
    : editorType === 'json'
      ? (typeof value === 'string' ? value : JSON.stringify(value, null, 2))
      : String(value);
  const [editValue, setEditValue] = useState(initialText);
  const [jsonError, setJsonError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    textareaRef.current?.focus();
    textareaRef.current?.select();
  }, []);

  const handleSave = useCallback(() => {
    if (editValue === '') {
      onSave(null);
      return;
    }
    if (editorType === 'json') {
      try {
        const parsed = JSON.parse(editValue);
        onSave(parsed);
      } catch (err) {
        setJsonError(err instanceof Error ? err.message : 'Invalid JSON');
        return;
      }
    } else {
      onSave(editValue);
    }
  }, [editValue, editorType, onSave]);

  const handlePrettyPrint = useCallback(() => {
    try {
      const parsed = JSON.parse(editValue);
      setEditValue(JSON.stringify(parsed, null, 2));
      setJsonError(null);
    } catch (err) {
      setJsonError(err instanceof Error ? err.message : 'Invalid JSON');
    }
  }, [editValue]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
    if (e.key === 's' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSave();
    }
  }, [onCancel, handleSave]);

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40" onClick={onCancel}>
      <div
        className="bg-[var(--dbgo-modal)] border border-[var(--border-default)] rounded-lg shadow-xl w-[560px] max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-[var(--border-subtle)]">
          <span className="text-sm text-[var(--text-primary)] font-medium">
            {editorType === 'json' ? 'Edit JSON' : 'Edit Text'}
          </span>
          <div className="flex items-center gap-2">
            {editorType === 'json' && (
              <button
                onClick={handlePrettyPrint}
                className="px-2 py-1 text-xs text-[var(--text-secondary)] hover:text-[var(--text-primary)] bg-[var(--dbgo-input)] border border-[var(--border-default)] rounded transition-colors"
              >
                Pretty Print
              </button>
            )}
          </div>
        </div>

        {/* Textarea */}
        <div className="flex-1 min-h-0 p-2">
          <textarea
            ref={textareaRef}
            value={editValue}
            onChange={(e) => { setEditValue(e.target.value); setJsonError(null); }}
            onKeyDown={handleKeyDown}
            className="w-full h-full min-h-[200px] max-h-[60vh] bg-[var(--dbgo-input)] border border-[var(--border-default)] rounded px-3 py-2 text-[var(--text-primary)] outline-none focus:border-[#4a7fff] resize-y"
            style={fontStyle}
            spellCheck={false}
          />
        </div>

        {/* Error / Footer */}
        <div className="flex items-center justify-between px-4 py-2 border-t border-[var(--border-subtle)]">
          <span className="text-xs text-[var(--accent-red)]">
            {jsonError ?? ''}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={onCancel}
              className="px-3 py-1.5 text-xs text-[var(--text-secondary)] hover:text-[var(--text-primary)] bg-[var(--dbgo-input)] border border-[var(--border-default)] rounded transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-3 py-1.5 text-xs text-white bg-[#4a7fff] hover:bg-[#5b8fff] rounded transition-colors"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
